import { useEffect, useRef, useState } from 'react';
import type { CheckClaim, EobCheck } from '../types';
import { amountAria, money } from '../utils';
import { Check, Spinner } from './common';
import { answerCheckClaim, postCheckClaim } from '../store';
import { PMS } from '@/features/shared/pms';

const STATUS_ORDER: Record<CheckClaim['status'], number> = { needs_you: 0, ready: 1, posted: 2 };

function Crop({ claim, onOpenPage }: { claim: CheckClaim; onOpenPage: () => void }) {
  return (
    <div style={{ flex: '1 1 260px' }}>
      <div role="img" aria-label={claim.pageAria} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, fontFamily: 'ui-monospace, monospace', fontSize: 12.5, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
        {claim.pageCrop}
      </div>
      <p className="po-meta" style={{ marginTop: 6 }}>Page {claim.pageNumber} of the fax · <button type="button" className="po-src" onClick={onOpenPage}>Open the full page</button></p>
    </div>
  );
}

function OpenClaim({ check, claim, onOpenPage, onPosted }: { check: EobCheck; claim: CheckClaim; onOpenPage: () => void; onPosted: () => void }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [posting, setPosting] = useState(false);
  const [step, setStep] = useState('');
  const [picked, setPicked] = useState<string | null>(claim.answered?.choice ?? null);
  const [other, setOther] = useState('');
  useEffect(() => { ref.current?.focus(); setPicked(claim.answered?.choice ?? null); }, [claim.id, claim.answered?.choice]);

  const disagree = claim.kind === 'smudged' && claim.status === 'needs_you';
  const needsAnswer = claim.status === 'needs_you';
  const canPost = !needsAnswer || picked !== null;

  const run = async (): Promise<void> => {
    setPosting(true);
    for await (const ev of postCheckClaim(check.id, claim.id)) {
      if ('step' in ev) setStep(ev.step);
    }
    setPosting(false);
    onPosted();
  };

  return (
    <section className="po-card">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <span className={`po-badge ${needsAnswer ? 'b-warn' : 'b-ok'}`}>{needsAnswer ? 'Needs you' : 'Ready'}</span>
        <h3 className="po-h3" tabIndex={-1} ref={ref}>{claim.headline}</h3>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <Crop claim={claim} onOpenPage={onOpenPage} />
        <div style={{ flex: '1 1 300px' }}>
          {disagree && claim.smudgeValues ? (
            <>
              <p className="po-label">Which amount is on the EOB?</p>
              <div className="po-options" role="radiogroup" aria-label="Which amount is on the EOB?">
                {claim.smudgeValues.map((v, i) => (
                  <button key={`v-${String(i)}`} type="button" role="radio" aria-checked={picked === `v${String(i)}`} className="po-option" style={picked === `v${String(i)}` ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined} onClick={() => { setPicked(`v${String(i)}`); }}>
                    {money(v.value)} <span className="po-meta">· {v.source}</span>
                  </button>
                ))}
                <button type="button" role="radio" aria-checked={picked === 'other'} className="po-option" style={picked === 'other' ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined} onClick={() => { setPicked('other'); }}>Something else</button>
              </div>
              {picked === 'other' ? <input className="po-field" style={{ marginTop: 8 }} placeholder="Enter the amount" value={other} onChange={(e) => { setOther(e.target.value); }} /> : null}
            </>
          ) : claim.question ? (
            <>
              <p className="po-label">{claim.question.text}</p>
              <div className="po-options" role="radiogroup" aria-label={claim.question.text}>
                {claim.question.options.map((o) => (
                  <button key={o.id} type="button" role="radio" aria-checked={picked === o.id} className="po-option" style={picked === o.id ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined} onClick={() => { setPicked(o.id); answerCheckClaim(check.id, claim.id, o.id); }}>{o.label}</button>
                ))}
              </div>
              {claim.question.meta ? <p className="po-meta" style={{ marginTop: 8 }}>{claim.question.meta}</p> : null}
            </>
          ) : (
            claim.ticks?.map((t, i) => <div key={`t-${String(i)}`} className="po-tickrow"><span className="po-tick" aria-hidden="true"><Check /></span><div>{t.text}</div></div>)
          )}
          {claim.kind === 'smudged' && claim.status === 'ready' ? (
            <p className="po-meta" style={{ marginTop: 8 }}>During training you confirm it. On autopilot, ELVA posts a smudged amount on its own only when all three agree.</p>
          ) : null}
        </div>
      </div>

      <table className="po-break" style={{ marginTop: 14 }}><tbody>
        {claim.breakdown.map((r, i) => (
          <tr key={`b-${String(i)}`} className={r.kind === 'total' ? 'total' : undefined}>
            <td>{r.label}{r.detail ? <span className="bk-note"> · {r.detail}</span> : null}</td>
            <td className="amt" aria-label={amountAria(r.amount)}>{money(r.amount)}</td>
          </tr>
        ))}
      </tbody></table>

      {posting ? (
        <div className="po-live" aria-live="polite" style={{ marginTop: 12 }}><Spinner /><span>{step || 'Posting…'}</span></div>
      ) : (
        <div className="po-actionbar" style={{ marginTop: 12 }}>
          <button type="button" className="po-btn po-btn-primary po-btn-lg" disabled={!canPost} onClick={() => { void run(); }}>Post to {PMS}</button>
          <button type="button" className="po-btn po-btn-ghost" onClick={onPosted}>I’ll post it myself</button>
          <span className="po-actionbar-meta">Then ELVA opens the next claim on this check.</span>
        </div>
      )}
    </section>
  );
}

export function CheckDetail({ check, onBack, onOpenPage, onSeePosted }: { check: EobCheck; onBack?: () => void; onOpenPage: (page: number) => void; onSeePosted: () => void }) {
  const sorted = [...check.claims].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  const firstOpen = sorted.find((c) => c.status !== 'posted')?.id ?? sorted[0]?.id ?? '';
  const [openId, setOpenId] = useState(firstOpen);
  const open = check.claims.find((c) => c.id === openId) ?? null;
  const postedCount = check.claims.filter((c) => c.status === 'posted').length;
  const allPosted = check.claims.every((c) => c.status === 'posted');

  const afterPost = (): void => {
    const next = [...check.claims].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]).find((c) => c.status !== 'posted');
    if (next) setOpenId(next.id);
  };

  return (
    <main className="po-detail">
      {onBack ? <button type="button" className="po-back" onClick={onBack}>← All payments</button> : null}
      <div className="po-detail-inner">
        <section className="po-claimhead">
          <div>
            <h2 className="po-patient" tabIndex={-1} style={{ fontSize: 22 }}>{check.payer} · check {check.checkNumber}</h2>
            <p className="po-chmeta">{check.source}</p>
            <p style={{ margin: '6px 0 0' }}><button type="button" className="po-src" onClick={() => { onOpenPage(1); }}>See the fax</button></p>
          </div>
          <div className="po-right"><div className="po-meta">Check amount</div><div className="po-bigfig num">{money(check.amount)}</div></div>
        </section>

        {check.handled ? (
          <section className="po-handled">
            <div className="po-handled-badge"><Check /> ELVA handled it</div>
            {check.handled.ticks.map((t, i) => (
              <div key={`h-${String(i)}`} className="po-tickrow"><span className="po-tick" aria-hidden="true"><Check /></span><div>{t.text}{t.links ? <span>{t.links.map((l) => <span key={l.label}> · <button type="button" className="po-src">{l.label}</button></span>)}</span> : null}</div></div>
            ))}
            <p className="po-meta" style={{ marginTop: 10 }}>{check.handled.note}</p>
          </section>
        ) : null}

        <section className="po-card">
          <div className="po-card-head">
            <h3 className="po-h3">{check.claims.length} claims on this check · {postedCount} posted</h3>
            <span className={check.addsUp.ok ? 'po-meta' : 'po-verdict-warn'}>{check.addsUp.text}</span>
          </div>
          <div className="po-tbl-wrap">
            <table className="po-tbl">
              <thead><tr><th>Patient</th><th>Visit</th><th>Procedures</th><th className="ta-right">Paid</th><th>Status</th><th className="ta-right">Open</th></tr></thead>
              <tbody>
                {sorted.map((c) => (
                  <tr key={c.id} style={c.id === openId ? { background: 'var(--psoft)' } : undefined}>
                    <td style={{ fontWeight: 600 }}>{c.patient}</td>
                    <td>{c.visit}</td>
                    <td>{c.procedures}</td>
                    <td className="ta-right num">{money(c.paid)}</td>
                    <td>
                      <span className={`po-badge ${c.status === 'needs_you' ? 'b-warn' : c.status === 'posted' ? 'b-ok' : 'b-info'}`}>{c.status === 'needs_you' ? 'Needs you' : c.status === 'posted' ? 'Posted ✓' : 'Ready'}</span>
                      {c.statusNote ? <div className="po-meta">{c.statusNote}</div> : null}
                    </td>
                    <td className="ta-right">{c.status === 'posted' ? null : <button type="button" className="po-btn po-btn-sm" onClick={() => { setOpenId(c.id); }}>Review</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="po-meta" style={{ marginTop: 10 }}>ELVA posts each claim into one {PMS} batch for this check, and closes the batch when all {check.claims.length} are posted and it balances.</p>
        </section>

        {allPosted ? (
          <section className="po-success">
            <h3 className="po-success-title">All {check.claims.length} claims are posted and the batch balances</h3>
            <div className="po-tickrow"><span className="po-tick" aria-hidden="true"><Check /></span><div>The {PMS} batch for this check is closed and it balances.</div></div>
            <p style={{ margin: '10px 0 0' }}><strong>Next: deposit the check.</strong> When your bank statement shows a deposit of {money(check.amount)}, ELVA ties it to this EOB in Bank deposits.</p>
            <div style={{ marginTop: 12 }}><button type="button" className="po-btn po-btn-sm" onClick={onSeePosted}>See the {check.claims.length} postings</button></div>
          </section>
        ) : open ? (
          <OpenClaim check={check} claim={open} onOpenPage={() => { onOpenPage(open.pageNumber); }} onPosted={afterPost} />
        ) : null}
      </div>
    </main>
  );
}
